import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from './apiService';
import { datadogLogs } from '@datadog/browser-logs';

interface StudentNameData {
  nameWasSpoken: boolean;
  firstName: string;
}

interface StudentNamesSpokenData {
  [studentId: string]: StudentNameData;
}

/**
 * Hook to fetch data about which student names have been spoken by the tutor
 * @param classId - The ID of the class
 * @param pollingInterval - Polling interval in milliseconds (default: 10000ms)
 * @returns Object containing the student names spoken data and loading/error states
 */
const useStudentNamesSpoken = (classId: string | number, pollingInterval = 10000) => {
  const [data, setData] = useState<StudentNamesSpokenData>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Use a ref to track if data is being fetched to prevent race conditions
  const isFetchingRef = useRef<boolean>(false);

  // Use a ref to store the data to avoid it being a dependency in useCallback
  const dataRef = useRef(data);
  
  // Update the ref whenever data changes
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  const fetchData = useCallback(async () => {
    // Prevent multiple concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    if (!classId) {
      setIsLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      
      // Only show loading indicator on initial load
      if (Object.keys(dataRef.current).length === 0) {
        setIsLoading(true);
      }
      
      // Use the API service to fetch data
      const responseData = await api.getStudentNamesSpoken(classId);
      
      if (responseData) {
        setData(responseData);
        setError(null);
      } else {
        setError(new Error('Failed to fetch student names spoken data'));
      }
      
      setIsLoading(false);
    } catch (err) {
      datadogLogs.logger.error('Error in useStudentNamesSpoken hook', {
        label: 'hook-failure',
        api: 'useStudentNamesSpoken',
        classId
      }, err as Error);
      
      setError(err instanceof Error ? err : new Error('Failed to fetch student names spoken data'));
      setIsLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, [classId]); // Remove data from dependencies

  // Use a ref to store the polling interval to prevent it from causing re-renders
  const pollingIntervalRef = useRef(pollingInterval);
  
  // Update the ref when the polling interval changes
  useEffect(() => {
    pollingIntervalRef.current = pollingInterval;
  }, [pollingInterval]);
  
  // Initial fetch
  useEffect(() => {
    if (classId) {
      fetchData();
    }
  }, [classId, fetchData]);
  
  // Set up polling with the specified interval
  useEffect(() => {
    // Skip if no classId or invalid polling interval
    if (!classId || pollingIntervalRef.current <= 0) return;
    
    // Create the interval
    const intervalId = setInterval(() => {
      fetchData();
    }, pollingIntervalRef.current);
    
    // Clean up the interval when the component unmounts or dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [classId, fetchData]); // Remove pollingInterval from dependencies

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
};

export default useStudentNamesSpoken;